import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { ResultsGallerySection } from "@/components/ResultsGallery";
import { IMG, SERVICES } from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";

export default function Services() {
  useSeo("Cleaning Services Perth | RD Cleaning Services", "Residential, commercial, end of lease, medical, education, industrial and specialised cleaning services across Perth, WA. Flexible options for every budget.");
  return (
    <>
      <PageHero
        eyebrow="Our Services"
        title="Cleaning Services for Every Space"
        subtitle="From everyday home cleaning to specialised commercial and industrial cleaning, we provide flexible solutions designed around your needs."
        image={IMG.equipment}
      >
        <Link to="/quote" className="focus-ring inline-flex items-center gap-2 rounded-full bg-teal-500 px-7 py-3.5 text-sm font-bold text-navy-deep transition-all hover:-translate-y-0.5 hover:bg-teal-400" data-testid="services-hero-quote-button">
          Get a Free Quote <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHero>

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl space-y-20 px-4 sm:px-6 lg:px-8">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id}>
              <article className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 === 1 ? "" : ""}`} data-testid={`service-block-${s.id}`}>
                <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="overflow-hidden rounded-3xl shadow-xl">
                    <img src={s.image} alt={`${s.name} — professional cleaning in Perth`} loading="lazy" className="aspect-[16/10] w-full object-cover" />
                  </div>
                </div>
                <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <span className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-teal-600">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">{s.name}</h2>
                  <p className="mt-4 leading-relaxed text-slate-600">{s.desc}</p>
                  <ul className="mt-6 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" /> {item}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <Link to="/quote" className="focus-ring inline-flex items-center gap-2 rounded-full bg-teal-600 px-6 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-teal-700" data-testid={`service-block-quote-${s.id}`}>
                      Get a Quote <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link to={s.link} className="focus-ring inline-flex items-center rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-navy transition-colors hover:border-teal-500 hover:text-teal-700" data-testid={`service-block-more-${s.id}`}>
                      Learn More
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <ResultsGallerySection
        slugs={["kitchen", "bathroom", "medical-clinic", "post-construction", "gym", "retail-tenancy", "windows", "warehouse"]}
        heading="See Our Cleaning Results"
        text="A look at the difference a professional clean makes across the spaces we service."
        bg="bg-slate-50"
        testid="services-gallery-section"
      />

      <CtaSection />
    </>
  );
}
